import { atom, useAtom } from "jotai";
import { useLayoutEffect, useMemo, useRef } from "react";

type ScrollPositionAtomItem = {
  /** The offset from the top of the list */
  offset: number;
  /**
   * A signature of the list items. If the signature does not match the `items` for the page, the stored offset is disregarded.
   *
   * This is used to avoid edge cases where the list items may have changed and the stored offset is stale.
   */
  signature: string;
};

const scrollPositionsAtom = atom<Record<string, ScrollPositionAtomItem>>({});

/**
 * Utility hook to support restoring scroll position on mount.
 *
 * This is all limited to in memory storage - a refresh higher in the navigation stack
 * will prevent this from running. This is because most of the screens we care to restore
 * scroll position on are infinite scrolls, and we if we refresh they won't have the data for later
 * "pages" in memory.
 */
export const useStoreScrollPosition = ({
  storageKey,
  getSignature,
  containerRef,
}: {
  storageKey: string;
  getSignature: () => string;
  containerRef: React.RefObject<HTMLElement | null>;
}) => {
  const [scrollPositions, setScrollPositions] = useAtom(scrollPositionsAtom);

  /** Scroll offset. null if there is no stored scroll position or the signature does not match. */
  const restorableScrollPositionOffset = useMemo(() => {
    const savedPosition = scrollPositions[storageKey];
    if (!savedPosition || getSignature() !== savedPosition.signature) return null;
    return savedPosition.offset;
  }, [scrollPositions[storageKey], getSignature, storageKey]);

  const prevContainerRef = useRef<HTMLElement | null>(null);

  // On unmount, store the current scroll position.
  useLayoutEffect(() => {
    // by the time the cleanup runs, the containerRef.current may be null
    // so, we just keep the most recent containerRef.current in a local ref, and pull the scrollTop from that
    if (containerRef.current) {
      prevContainerRef.current = containerRef.current;
    }

    return () => {
      const container = prevContainerRef.current;
      if (!container) return;

      const offset = container.scrollTop;
      const signature = getSignature();

      requestAnimationFrame(() => {
        setScrollPositions((prev) => {
          if (offset === prev[storageKey]?.offset && signature === prev[storageKey]?.signature)
            return prev;

          return {
            ...prev,
            [storageKey]: { offset, signature },
          };
        });
      });
    };
  }, [containerRef.current, getSignature, setScrollPositions, storageKey]);

  return { restorableScrollPositionOffset };
};

/**
 * Utility hook to automatically restore scroll position for a given ref on mount. Will run exactly once.
 *
 * TODO: this is very hacky. We should pull in a router that can actually do this for us, because this ain't great.
 * e.g. when you nav "forward" to a screen like home, it will still try to restore.
 * https://linear.app/big-basin-labs/issue/BIG-980/use-a-router-with-better-featureset
 *
 * @see useStoreScrollPosition
 */
export const useRestoreScrollPosition = ({
  storageKey,
  containerRef,
  getSignature,
}: {
  storageKey: string;
  containerRef: React.RefObject<HTMLElement | null>;
  getSignature: () => string;
}) => {
  const { restorableScrollPositionOffset } = useStoreScrollPosition({
    storageKey,
    containerRef,
    getSignature,
  });
  const hasSetInitialScrollPosition = useRef<boolean>(false);

  useLayoutEffect(() => {
    if (hasSetInitialScrollPosition.current) return; // Regardless of what scroll we performed, we only do one initial scroll for the duration of the component's lifetime.
    if (restorableScrollPositionOffset === null) return; // If no scroll position is stored, do nothing.
    if (!containerRef.current) return; // If the container is not mounted, do nothing.

    containerRef.current.scrollTo({ top: restorableScrollPositionOffset, behavior: "instant" });

    hasSetInitialScrollPosition.current = true;
  }, [restorableScrollPositionOffset, containerRef]);
};
